import {
  CollaboratorAccessEnumValue,
  CollaboratorRolesEnumValue,
  DetailValueTypeEnumValue,
  JobPriorityEnumValue,
} from "@panda0/panda0-graphql/defs";
import {
  formatDate,
  HIREPANDA_FOUNDING_DATE,
  PAGINATION_LIMIT,
} from "@panda0/panda0-utils";
import { z } from "zod";

import { eventIntervals, intervals } from "@/lib/analytics/constants";
import { sortOrder } from "./analytics";
import { LinkSchema } from "./links";
import { booleanQuerySchema, getPaginationQuerySchema } from "./misc";
import { TagSchema } from "./tags";
import { parseDateSchema } from "./utils";

export const CollaboratorSchema = z.object({
  id: z.string().optional(), // Will be empty for new collaborators
  userId: z.string().optional(), // Internal user ID if available
  email: z.string().email(), // Required for all collaborators
  name: z.string().optional(), // Optional name for display
  role: z.custom<CollaboratorRolesEnumValue>(),
  access: z.custom<CollaboratorAccessEnumValue>(),
  isExternal: z.boolean().default(false), // Whether this is an external collaborator
});

export const JobLinksSchema = z.object({
  jobId: z.string(),
  linkId: z.string(),
  createdAt: z
    .preprocess(
      (val) => (val instanceof Date ? val.toISOString() : val),
      z.string().datetime({ offset: true })
    )
    .pipe(z.coerce.date())
    .optional(),
  updatedAt: z
    .preprocess(
      (val) => (val instanceof Date ? val.toISOString() : val),
      z.string().datetime({ offset: true })
    )
    .pipe(z.coerce.date())
    .optional(),
  deletedAt: z
    .preprocess(
      (val) => (val instanceof Date ? val.toISOString() : val),
      z.string().datetime({ offset: true })
    )
    .pipe(z.coerce.date())
    .nullable()
    .optional(),
});

export const EmploymentTypeEnumValue = {
  fulltime: "FULLTIME",
  parttime: "PARTTIME",
  contract: "CONTRACT",
  internship: "INTERNSHIP",
} as const;
export type EmploymentTypeEnumValue =
  (typeof EmploymentTypeEnumValue)[keyof typeof EmploymentTypeEnumValue];

export const WorkModeEnumValue = {
  remote: "REMOTE",
  hybrid: "HYBRID",
  office: "OFFICE",
} as const;
export type WorkModeEnumValue =
  (typeof WorkModeEnumValue)[keyof typeof WorkModeEnumValue];

export const ScreeningQuestionOptionsSchema = z.object({
  SCREENING_OPTIONS: z.array(z.string()).optional(),
});

export const EmailTemplateSchema = z.object({
  name: z.string().min(1, "Template name is required").optional(),
  subject: z.string().min(1, "Subject is required"),
  body: z.string().min(1, "Body is required"),
  signature: z.string().optional(),
  useSignature: z.boolean().optional(),
  isDefault: z.boolean().optional(),
});

export const EmailTemplateUpdateSchema = z.object({
  name: z.string().min(1, "Template name is required").optional(),
  subject: z.string().min(1, "Subject is required").optional(),
  body: z.string().min(1, "Body is required").optional(),
  signature: z.string().optional(),
  useSignature: z.boolean().optional(),
  isDefault: z.boolean().optional(),
});

export const JobBasicInfoSchema = z.object({
  title: z.string().min(1, "Job title is required"),
  description: z.string().default(""),
  departmentId: z.string().min(1, "Department is required"),
  folderId: z.string().optional().nullable(),
  positions: z.preprocess(
    Number,
    z.number().min(1, "Must have at least 1 opening")
  ),
  experienceStart: z.preprocess(Number, z.number().min(0)),
  experienceEnd: z.preprocess(Number, z.number().min(0)),
  campaignStart: z
    .preprocess(
      (val) => (val instanceof Date ? val.toISOString() : val),
      z.string().datetime({ offset: true })
    )
    .pipe(z.coerce.date()),
  campaignEnd: z
    .preprocess(
      (val) => (val instanceof Date ? val.toISOString() : val),
      z.string().datetime({ offset: true })
    )
    .optional()
    .pipe(z.coerce.date()),
  priority: z
    .custom<JobPriorityEnumValue>()
    .default(JobPriorityEnumValue.medium)
    .optional(),
  key: z.string().min(1, "Key is required"),
  url: z.string().url(),
  tags: TagSchema.array().nullable().describe("The tags assigned to the job."),
  trackConversion: z.boolean().nullable().default(false).optional(),
  // External Job Information
  externalId: z.string().nullable().optional().default(""),
  JOBSETTINGS_APPLICATION_URL: z.string().nullable().optional().default(""),
  collaborators: z.array(CollaboratorSchema).optional().default([]),
});

export const SubskillDataSchema = z.object({
  name: z.string(),
  weightage: z.preprocess(Number, z.number().min(0)),
});

export const CognitiveSkillDataSchema = z.object({
  id: z.string(),
  skill: z.string(),
  weightage: z.preprocess(Number, z.number().min(0)),
  description: z.string(),
});

// Skills Step Schema
export const SkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  weightage: z.preprocess(Number, z.number().min(0).max(100)),
  subskills: z.array(SubskillDataSchema),
  description: z.string().optional(),
});

export const CognitiveSkillSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Skill name is required"),
  weightage: z.preprocess(Number, z.number().min(0).max(100)),
  description: z.string().nullable().optional().default(""),
});

export const SkillsStepSchema = z.object({
  skills: z.object({
    technicalSkills: z.array(SkillSchema),
    cognitiveSkills: z.array(CognitiveSkillSchema),
  }),
});

export const QuizShareResultsEnumValue = {
  immediately: "IMMEDIATELY",
  afterJobClosed: "AFTER_JOB_CLOSED",
  never: "NEVER",
} as const;
export type QuizShareResultsEnumValue =
  (typeof QuizShareResultsEnumValue)[keyof typeof QuizShareResultsEnumValue];

const ExperienceRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
});

const PandaQuizQuestionSkillTagSchema = z.union([
  z.string(),
  z
    .object({
      name: z.string(),
      subSkills: z.array(z.string()).optional(),
      skill: z
        .union([
          z.string(),
          z.object({
            name: z.string(),
            sub_skills: z.array(z.string()).optional(),
          }),
        ])
        .optional(),
    })
    .transform((tag) => {
      // Transform to consistent format
      if (tag.skill) {
        const skillName =
          typeof tag.skill === "string" ? tag.skill : tag.skill.name;
        const subSkills =
          typeof tag.skill === "string" ? [] : tag.skill.sub_skills || [];
        return { name: skillName, subSkills };
      }
      return { name: tag.name, subSkills: tag.subSkills || [] };
    }),
]);

// Update the option schema to handle both field names
const PandaQuizQuestionOptionSchema = z.object({
  option: z.string(),
  relevancy: z.number(),
  score: z.number(),
  experienceRange: z.union([
    ExperienceRangeSchema,
    z
      .object({
        min: z.number(),
        max: z.number(),
      })
      .transform((range) => ({
        min: range.min,
        max: range.max,
      })),
  ]),
  skillTags: z.array(PandaQuizQuestionSkillTagSchema).optional(),
});

// Update the question schema to handle all variations
export const PandaQuizQuestionSchema = z.preprocess(
  (data: any) => {
    if (!data) return data;

    // Normalize to consistent format
    return {
      question: data.question,
      weightage: data.weightage,
      experienceLevel: data.experienceLevel || data.experience_level || "",
      taxonomyTags:
        data.taxonomyTags ||
        data.bloomsTaxonomyTags ||
        data.blooms_taxonomy_tags ||
        [],
      skillTags: data.skillTags || data.skill_tags || [],
      options: (data.options || []).map((option: any) => ({
        option: option.option,
        relevancy: option.relevancy,
        score: option.score,
        experienceRange: option.experienceRange,
        skillTags: option.skillTags || [],
      })),
      skillName: data.skillName || "",
    };
  },
  z.object({
    question: z.string(),
    weightage: z.number(),
    experienceLevel: z.union([z.string(), z.number()]),
    taxonomyTags: z.array(z.string()),
    skillTags: z.array(
      z.union([
        z.string(),
        z.object({
          name: z.string(),
          subSkills: z.array(z.string()).optional(),
        }),
      ])
    ),
    options: z.array(
      z.object({
        option: z.string(),
        relevancy: z.number(),
        score: z.number(),
        experienceRange: z.object({
          min: z.number(),
          max: z.number(),
        }),
        skillTags: z
          .array(
            z.union([
              z.string(),
              z.object({
                name: z.string(),
                subSkills: z.array(z.string()).optional(),
              }),
            ])
          )
          .optional(),
      })
    ),
    skillName: z.string().optional(),
  })
);

export const PandaQuizStepSchema = z.object({
  pandaQuiz: z.object({
    questions: z.array(PandaQuizQuestionSchema),
  }),
});

export const ProctoringSettingsSchema = z.object({
  PROCTORING_ENABLED: z.boolean().default(true),
  PROCTORING_MULTIFACE_DETECTION: z.boolean().default(true),
  PROCTORING_VOICE_MONITORING: z.boolean().default(true),
  PROCTORING_EYE_TRACKING: z.boolean().default(true),
  PROCTORING_LIVENESS_ENABLED: z.boolean().default(true),
  PROCTORING_PERIODIC_SNAPSHOTS: z.boolean().default(true),
  PROCTORING_RED_FLAG_SNAPSHOTS: z.boolean().default(true),
  PROCTORING_BLOCK_SCREEN_SHARING: z.boolean().default(true),
  PROCTORING_OBJECT_DETECTION: z.boolean().default(true),
  PROCTORING_BROWSER_FOCUS: z.boolean().default(true),
});

export declare const ScreeningQuestionAnswerTypeEnumValue: {
  readonly oneline: "ONELINE";
  readonly multiline: "MULTILINE";
  readonly radiobutton: "RADIOBUTTON";
  readonly checkbox: "CHECKBOX";
};
export type ScreeningQuestionAnswerTypeEnumValue =
  (typeof ScreeningQuestionAnswerTypeEnumValue)[keyof typeof ScreeningQuestionAnswerTypeEnumValue];

export const ScreeningQuestionSchema = z.object({
  SCREENING_TEXT: z.string(),
  SCREENING_ANSWERTYPE: z.custom<ScreeningQuestionAnswerTypeEnumValue>(),
  SCREENING_ISREQUIRED: z.coerce.boolean().default(false),
  SCREENING_OPTIONS: z.array(z.string()).optional(),
});

export const JobDetailsSchema = z.object({
  JOBINFORMATION_SALARYCURRENCY: z.string().default("INR"),
  JOBINFORMATION_SALARYMIN: z.string().optional(),
  JOBINFORMATION_SALARYMAX: z.string().optional(),
  JOBINFORMATION_WORKTYPE: z.string().optional(),
  JOBINFORMATION_WORKMODE: z.string().optional(),
  JOBINFORMATION_WORKLOCATION: z.string().optional(),
  JOBINFORMATION_SHORTDESCRIPTION: z.string().optional(),
  JOBINFORMATION_PERKSBENEFITS: z.string().optional(),
  JOBINFORMATION_OGDESCRIPTION: z.string().optional(),
});

export const FormDetailsSchema = z.object({
  FORMSETTINGS_RESUMEREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_CONTACTNUMBEREQUIRED: z.coerce.boolean().default(true),
  FORMSETTINGS_LOCATIONREQUIRED: z.coerce.boolean().default(true),
  FORMSETTINGS_LINKEDINREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_GITHUBREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_EDUCATIONREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_CTCREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_CANDIDATECONSENTREQUIRED: z.coerce.boolean().default(false),
  FORMSETTINGS_CONSENTTEXT: z.string().optional(),

  ISREQUIRED_FORMSETTINGS_RESUMEREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_CONTACTNUMBEREQUIRED: z.coerce.boolean().default(true),
  ISREQUIRED_FORMSETTINGS_LOCATIONREQUIRED: z.coerce.boolean().default(true),
  ISREQUIRED_FORMSETTINGS_LINKEDINREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_GITHUBREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_EDUCATIONREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_CTCREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_CANDIDATECONSENTREQUIRED: z.coerce.boolean().default(false),
  ISREQUIRED_FORMSETTINGS_CONSENTTEXT: z.coerce.boolean().default(false),
});

export const ReferralRewardsSchema = z.object({
  REFERRAL_REWARDTYPE: z.string().optional().default("CASH"),
  REFERRAL_DETAILS: z.string().optional().default(""),
  REFERRAL_CURRENCY: z.string().optional().default("INR"),
  REFERRAL_AMOUNT: z.string().optional().default("0"),
  REFERRAL_MUSTBEHIRED: z.coerce.boolean().optional().default(false),
  REFERRAL_MUSTCOMPLETEPROBATION: z.coerce.boolean().optional().default(false),
});

export const QuizSettingsSchema = z.object({
  QUIZ_SHARERESULTS: z.custom<QuizShareResultsEnumValue>(),
  QUIZ_TIMELIMIT: z.string().optional(),
  QUIZ_PASSINGSCORE: z.string().optional(),
  QUIZ_RANDOMIZEQUESTIONS: z.coerce.boolean().optional(),
  QUIZ_ALLOWMULTIPLEATTEMPTS: z.coerce.boolean().optional(),
  QUIZ_SHOWCORRECTANSWERS: z.coerce.boolean().optional(),
});

export const SubmissionSettingsSchema = z.object({
  QUIZSUBMISSION_RESUME_UPLOAD: z.coerce.boolean().default(true),
  QUIZSUBMISSION_SHARE_RESULTS: z.string().default("NEVER"),
  QUIZSUBMISSION_EMAIL_CONFIRMATION: z.coerce.boolean().optional(),
  QUIZSUBMISSION_NOTIFY_HIRING_TEAM: z.coerce.boolean().optional(),
  QUIZ_GAME_PLAY: z.coerce.boolean().optional(),
});

export const VideoResumeSchema = z.object({
  QUIZSUBMISSION_VIDEO_RESUME: z.coerce.boolean().default(false),
  QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS: z.string().optional(),
  QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION: z.string().optional(),
});

// Update CustomizeStepSchema to include all these new schemas
export const CustomizeStepSchema = z.object({
  screeningQuestions: z.array(ScreeningQuestionSchema).default([]),
  proctoringSettings: ProctoringSettingsSchema.optional(),
  submissionSettings: SubmissionSettingsSchema.optional(),
  jobDetails: JobDetailsSchema.optional(),
  videoResume: VideoResumeSchema.optional(),
  formDetails: FormDetailsSchema.optional(),
  referralRewards: ReferralRewardsSchema.optional(),
  quizSettings: QuizSettingsSchema.optional(),
});

// Share Step Schema
export const ShareStepSchema = z.object({
  pandaQuizShare: z.object({
    shortLink: z.string(),
    candidates: z.array(
      z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string().optional(),
        email: z.string().email(),
      })
    ),
    emailTemplate: z.object({
      subject: z.string(),
      body: z.string(),
      signature: z.string().optional(),
      useSignature: z.boolean(),
    }),
  }),
});

// Complete Job Schema combining all steps
export const JobSchema = z
  .object({
    folderId: z.string(),
  })
  .merge(
    z.object({
      basic: JobBasicInfoSchema.required(),
    })
  )
  .merge(SkillsStepSchema)
  .merge(PandaQuizStepSchema)
  .merge(CustomizeStepSchema)
  .merge(ShareStepSchema);

// Job Webhook Schema
export const JobWebhookSchema = z.object({
  jobId: z.string(),
  webhookId: z.string(),
});

// For fetching jobs
const JobsQuerySchema = z.object({
  tagIds: z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : v.split(",")))
    .optional()
    .describe("The tag IDs to filter the jobs by."),
  tagId: z.string().optional().describe("The tag ID to filter the jobs by."),
  departmentIds: z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : v.split(",")))
    .optional()
    .describe("The department ids to filter the jobs by."),
  tagNames: z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : v.split(",")))
    .optional()
    .describe(
      "The unique name of the tags assigned to the job (case insensitive)."
    ),
  folderId: z
    .string()
    .optional()
    .describe("The folder ID to filter the jobs by."),
  status: z.string().optional().describe("The status to filter the jobs by."),
  search: z
    .string()
    .optional()
    .describe(
      "The search term to filter the jobs by. The search term will be matched against the title."
    ),
  userId: z.string().optional().describe("The user ID to filter the jobs by."),
  tenantId: z
    .string()
    .optional()
    .describe(
      "The ID of the tenant that created the link inside your system. If set, will only return jobs for the specified tenant."
    ),
  showArchived: booleanQuerySchema
    .optional()
    .default("false")
    .describe(
      "Whether to include archived jobs in the response. Defaults to `false` if not provided."
    ),
  withTags: booleanQuerySchema
    .optional()
    .default("false")
    .describe(
      "DEPRECATED. Filter for jobs that have at least one tag assigned to them."
    )
    .openapi({ deprecated: true }),
});

const sortBy = z
  .enum([
    "campaignEnd",
    "createdAt",
    "priority",
    "clicks",
    "candidates",
    "saleAmount",
    "lastClicked",
  ])
  .optional()
  .default("campaignEnd")
  .describe("The field to sort the jobs by. The default is `campaignEnd`.");

export const getJobsQuerySchemaBase = JobsQuerySchema.merge(
  z.object({
    sortBy,
    sortOrder: z
      .enum(["ASC", "DESC"])
      .optional()
      .default("DESC")
      .describe("The sort order. The default is `DESC`."),
    sort: sortBy
      .openapi({ deprecated: true })
      .describe("DEPRECATED. Use `sortBy` instead."),
  })
).merge(getPaginationQuerySchema({ pageSize: 100 }));

export const getJobsQuerySchemaExtended = getJobsQuerySchemaBase.merge(
  z.object({
    // Only HirePanda UI uses the following query parameters
    includeUser: booleanQuerySchema.default("false"),
    includeWebhooks: booleanQuerySchema.default("false"),
    includeDashboard: booleanQuerySchema.default("false"),
    jobIds: z
      .union([z.string(), z.array(z.string())])
      .transform((v) => (Array.isArray(v) ? v : v.split(",")))
      .optional()
      .describe("Job IDs to filter by."),
    linkIds: z
      .union([z.string(), z.array(z.string())])
      .transform((v) => (Array.isArray(v) ? v : v.split(",")))
      .optional()
      .describe("Link IDs to filter by."),
    partnerId: z.string().optional().describe("Partner ID to filter by."),
  })
);

export const getJobsCountQuerySchema = JobsQuerySchema.merge(
  z.object({
    groupBy: z
      .union([
        z.literal("status"),
        z.literal("priority"),
        z.literal("departmentId"),
        z.literal("tagId"),
        z.literal("userId"),
        z.literal("folderId"),
      ])
      .optional()
      .describe("The field to group the jobs by."),
  })
);

export const quizGptResponse = z.object({
  type: z.string(),
  text: z.object({ value: z.string(), annotations: z.array(z.unknown()) }),
});

export const insightsQuerySchema = z.object({
  interval: z
    .enum(intervals)
    .optional()
    .describe(
      "The interval to retrieve analytics for. If undefined, defaults to 24h."
    ),
  start: parseDateSchema
    .refine((value: Date) => value >= HIREPANDA_FOUNDING_DATE, {
      message: `The start date cannot be earlier than ${formatDate(HIREPANDA_FOUNDING_DATE)}.`,
    })
    .optional()
    .describe(
      "The start date and time when to retrieve analytics from. Takes precedence over `interval`."
    ),
  end: parseDateSchema
    .optional()
    .describe(
      "The end date and time when to retrieve analytics from. If not provided, defaults to the current date. Takes precedence over `interval`."
    ),
  page: z.coerce.number().default(1),
  limit: z.coerce.number().default(PAGINATION_LIMIT),
  sortOrder,
  sortBy: z
    .enum(["timestamp"])
    .optional()
    .default("timestamp")
    .describe("The field to sort the events by. The default is `timestamp`."),
});

// Define valid categories and sort fields
const ValidCategories = [
  "summary",
  "candidates",
  "candidate-comparison",
  "quiz-performance",
  "sources",
] as const;
const ValidSortFields = ["score", "name", "date", "status"] as const;

export const insightsSummarySchema = z.object({
  interval: z
    .enum(intervals)
    .optional()
    .describe(
      "The interval to retrieve analytics for. If undefined, defaults to 24h."
    ),
  // Category parameter
  category: z.enum(ValidCategories).optional().default("summary"),

  start: parseDateSchema
    .refine((value: Date) => value >= HIREPANDA_FOUNDING_DATE, {
      message: `The start date cannot be earlier than ${formatDate(HIREPANDA_FOUNDING_DATE)}.`,
    })
    .optional()
    .describe(
      "The start date and time when to retrieve analytics from. Takes precedence over `interval`."
    ),
  end: parseDateSchema
    .optional()
    .describe(
      "The end date and time when to retrieve analytics from. If not provided, defaults to the current date. Takes precedence over `interval`."
    ),

  // Pagination parameters
  page: z
    .string()
    .optional()
    .default("1")
    .transform((val) => parseInt(val))
    .pipe(z.number().positive().int()),

  pageSize: z
    .string()
    .optional()
    .default("20")
    .transform((val) => parseInt(val))
    .pipe(z.number().positive().int().max(100)),

  // Search parameter
  search: z.string().optional().default(""),

  // Sort parameters
  sortBy: z.enum(ValidSortFields).optional().default("score"),

  sortOrder: sortOrder,

  // Candidate IDs array
  candidateIds: z.array(z.string()).optional().default([]),
});
